import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getTime, scrollToBottom } from "./ChatHandler";
import { Message } from "./ChatInterface";
import { DownOutlined, CloseOutlined } from "@ant-design/icons";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import {
  fetchCategories,
  generateTemplate,
  fetchCampaignListsAndCompany,
} from "src/services/EngagementAPIs";
import { useNavigate } from "react-router-dom";
import { Spin, Avatar, message } from "antd";
import { autoCorrectInput } from "src/utils/corrections";
import ChatInput from "./ChatInput";
import { Category } from "../../components/CategoryStep";
import {
  ChartLine,
  CircleDollarSign,
  Expand,
  ImagePlus,
  ListPlus,
  Search,
  Volume2,
} from "lucide-react";
import { CustomCampaignForm } from "../../components/CustomCampaignForm";
import { campaigns, subCampaigns, CampaignKey } from "src/utils/constants";
import { translateCampaignLabel } from "src/utils/campaignLabels";
import {
  handleQuickSchedule,
  handleConfirmSchedule,
  handleCancelSchedule,
  handleScheduleConfirmation,
} from "src/utils/ScheduleUtils";
import { createMessageHandler } from "src/handlers/messageHandlers";
import { resetChatSession } from "src/services/ChatbotAPI";
import { useLanguage } from "src/hooks/useLanguage";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  audienceListMatchesSearch,
  CREATE_NEW_LIST_NAME,
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";
const TONE_OPTIONS = [
  "Friendly",
  "Professional",
  "Urgent",
  "Playful",
  "Informative",
] as const;

const Chatbot = () => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const isArabic = (i18n.resolvedLanguage || i18n.language || "en")
    .toLowerCase()
    .startsWith("ar");
  const chatbotRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const [ctaLink, setCtaLink] = useState("");
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [listCreationMode, setListCreationMode] = useState<
    "standalone" | "campaign" | null
  >(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isScheduledOrSent, setIsScheduledOrSent] = useState(false);
  const isScheduledOrSentRef = useRef(isScheduledOrSent);
  const [conversationStep, setConversationStep] = useState<
    | "askName"
    | "impressionsStep"
    | "roasStep"
    | "conversionsStep"
    | "engagementStep"
    | "askAudience"
    | "askWebsite"
    | "fetchCategories"
    | "selectCategory"
    | "categoryStep"
    | "selectCampaign"
    | "selectSubCampaign"
    | "fillPrompt"
    | "customCampaignForm"
    | "confirmSend"
    | "sendOption"
    | "scheduleSend"
    | "schedulePicker"
    | "confirmCampaign"
    | "confirmation"
    | "autoFetchCategories"
    | null
  >(null);
  const clearedRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(
    null,
  );
  const [campaignName, setCampaignName] = useState("");
  const [audience, setAudience] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignKey | null>(
    null,
  );
  const user_id = localStorage.getItem("user_id");
  const tenant_id = localStorage.getItem("tenant_id") || "";
  const [persona, setPersona] = useState("");
  const [seasonalTag, setSeasonalTag] = useState("");
  const [offer, setOffer] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [position, setPosition] = useState(() => ({
    x: isRTL ? 20 : window.innerWidth - 100,
    y: window.innerHeight - 100,
  }));
  const [selectedFile, setSelectedFile] = useState<File | File[] | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const dragging = useRef(false);
  const dragged = useRef(false);
  const [templateJSON, setTemplateJSON] = useState<any>(null);
  const offset = useRef({ x: 0, y: 0 });
  const [selectedSubCampaign, setSelectedSubCampaign] = useState<string | null>(
    null,
  );
  const [prompt, setPrompt] = useState("");
  const [selectedTones, setSelectedTones] = useState<string[]>([]);
  const [isSendOptionDisabled, setIsSendOptionDisabled] = useState(false);
  const [isScheduleBoxDisabled, setIsScheduleBoxDisabled] = useState(false);
  const [audiences, setAudiences] = useState<AudienceListOption[]>([]);
  const [audienceListSearch, setAudienceListSearch] = useState("");
  const [, setInputDisabled] = useState(false);
  const [queryLimitApiMessage, setQueryLimitApiMessage] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<any>(null);

  const filteredCategoriesForSearch = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) {
      return categories;
    }
    return categories.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        (c.url && c.url.toLowerCase().includes(q)),
    );
  }, [categories, categorySearch]);

  const visibleCategories = useMemo(
    () => filteredCategoriesForSearch.slice(0, visibleCount),
    [filteredCategoriesForSearch, visibleCount],
  );

  const quickActions: {
    value: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: "Create New Campaign",
      title: t("chatScreen.quickActionCreateCampaignTitle"),
      subtitle: t("chatScreen.quickActionCreateCampaignSubtitle"),
      icon: <Volume2 size={20} />,
    },
    {
      value: "Impressions",
      title: t("chatScreen.quickActionAnalyseScreenshotTitle"),
      subtitle: t("chatScreen.quickActionAnalyseScreenshotSubtitle"),
      icon: <ImagePlus size={20} />,
    },
    {
      value: "Engagement & Likes",
      title: t("chatScreen.quickActionEngagementImpressionsTitle"),
      subtitle: t("chatScreen.quickActionEngagementImpressionsSubtitle"),
      icon: <ChartLine size={20} />,
    },
    {
      value: "Return on Ad Spend",
      title: t("chatScreen.quickActionRoasTitle"),
      subtitle: t("chatScreen.quickActionRoasSubtitle"),
      icon: <CircleDollarSign size={20} />,
    },
    {
      value: "Create New List",
      title: t("chatScreen.quickActionCreateListTitle"),
      subtitle: t("chatScreen.quickActionCreateListSubtitle"),
      icon: <ListPlus size={20} />,
    },
  ];

  const filteredExistingAudiences = useMemo(
    () =>
      audiences
        .filter(a => a.name !== CREATE_NEW_LIST_NAME)
        .filter(a => audienceListMatchesSearch(a, audienceListSearch)),
    [audiences, audienceListSearch],
  );

  useEffect(() => {
    if (conversationStep !== "askAudience") {
      setAudienceListSearch("");
    }
  }, [conversationStep]);

  useEffect(() => {
    setVisibleCount(10);
  }, [categorySearch]);

  useEffect(() => {
    if (categories.length === 0) {
      setCategorySearch("");
    }
  }, [categories.length]);

  useEffect(() => {
    resetChatSession();
  }, []);

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [query, loading]);

  useEffect(() => {
    isScheduledOrSentRef.current = isScheduledOrSent;
  }, [isScheduledOrSent]);

  const apiLang = getApiLangFromI18n(i18n);

  useEffect(() => {
    const createNewListOption: AudienceListOption = {
      name: CREATE_NEW_LIST_NAME,
      displayName: t("chatScreen.createNewList"),
    };

    const loadAudiences = async () => {
      try {
        const apiKey = localStorage.getItem("api_key") || "";
        const tenantId = localStorage.getItem("tenant_id") || "";

        if (!apiKey || !tenantId) {
          message.error(t("chatScreen.missingApiCredentials"));
          return;
        }

        const response = await fetchCampaignListsAndCompany(tenantId, apiKey, apiLang);
        setAudiences(
          registerAudienceListLabels([
            ...toAudienceListOptions(response),
            createNewListOption,
          ]),
        );
      } catch (error) {
        const listOption = (key: string) =>
          t(`audienceOverview.createSegment.listOptions.${key}`);
        setAudiences(
          registerAudienceListLabels([
            { name: "Loyal Customers", displayName: listOption("loyalCustomers") },
            { name: "High Spenders", displayName: listOption("highSpenders") },
            { name: "Cart Abandoners", displayName: listOption("cartAbandoners") },
            createNewListOption,
          ]),
        );
      }
    };

    loadAudiences();
  }, [t, apiLang]);

  useEffect(() => {
    if (isOpen) {
      const listener = (event: MouseEvent) => {
        if (
          chatbotRef.current &&
          !chatbotRef.current.contains(event.target as Node) &&
          !(event.target as HTMLElement).closest(".sidebar")
        ) {
          setIsOpen(false);
          setMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", listener);
      return () => document.removeEventListener("mousedown", listener);
    }
  }, [isOpen]);

  const formatCategoryName = (name: string) => {
    return name.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleClearChat = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    clearedRef.current = true;
    resetChatSession();
    setQuery([]);
    setFirstMessageSent(false);
    setConversationStep(null);
    setSuggestedQuestions([]);
    setIsFetching(false);
    setLoading(false);
    setMenuOpen(false);
    setQueryLimitApiMessage(undefined);
  };

  const handleAutoCorrectChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (val: string) => void,
  ) => {
    const value = e.target.value;
    const lastChar = value.slice(-1);

    if ([" ", ".", ",", "!", "?"].includes(lastChar)) {
      setter(autoCorrectInput(value));
    } else {
      setter(value);
    }
  };

  const handleCancelClick = () => {
    if (isScheduledOrSentRef.current) {
      message.warning(t("chatScreen.sentCannotEdit"));
      return;
    }

    handleCancelSchedule(setScheduledDate, setScheduledTime, setQuery);
  };

  const triggerAutoFetchCategories = async () => {
    setLoading(true);
    setIsFetching(true);

    try {
      const apiKey = localStorage.getItem("api_key") || "";
      const tenantId = parseInt(localStorage.getItem("tenant_id") || "0", 10);

      const response = await fetchCategories(getApiLangFromI18n(i18n));

      const fetchedCategories: Category[] =
        response?.website_categories?.map((url: string) => {
          const parts = url.split("/");
          const name = parts[parts.length - 1] || url;
          return { name, url };
        }) || [];

      setCategories(fetchedCategories);

      if (fetchedCategories.length > 0) {
        const categoryMessage: Message = {
          sender: "bot",
          timestamp: getTime(),
          type: "categoryStep",
          categories: fetchedCategories,
        };

        setQuery(prev => [...prev, categoryMessage]);
        setConversationStep("selectCategory");
      } else {
        const botMessage: Message = {
          sender: "bot",
          text: t("chatScreen.noCategoriesDetected"),
          timestamp: getTime(),
        };
        setQuery(prev => [...prev, botMessage]);
        setConversationStep(null);
      }
    } catch (err) {
      const botMessage: Message = {
        sender: "bot",
        text: t("chatScreen.failedToFetchCategories"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep(null);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleQueryLimitReached = useCallback((backendDetail: string) => {
    const messageFromApi = backendDetail.trim();
    setQueryLimitApiMessage(messageFromApi);
    setQuery(prev => {
      if (prev.some(msg => msg.type === "limitNotice")) {
        return prev;
      }
      if (!messageFromApi) {
        return prev;
      }
      return [
        ...prev,
        {
          sender: "bot",
          type: "limitNotice",
          text: messageFromApi,
          timestamp: getTime(),
        },
      ];
    });
    setLoading(false);
    setIsFetching(false);
    setSuggestedQuestions([]);
  }, []);

  const messageHandler = createMessageHandler({
    textToSend: input,
    conversationStep,
    input,
    selectedFile,
    previewURL,
    campaignName,
    audience,
    generatedHtml,
    generatedPreviewUrl,
    listCreationMode,
    user_id,
    tenant_id,
    clearedRef,
    navigate,
    setQuery,
    setInput,
    setConversationStep,
    setCampaignName,
    setAudience,
    setListCreationMode,
    setLoading,
    setIsFetching,
    setFirstMessageSent,
    setSuggestedQuestions,
    setSelectedCategories,
    setSelectedCampaign,
    setSelectedSubCampaign,
    setCategories,
    triggerAutoFetchCategories,
    templateJSON,
    setTemplateJSON,
    onQueryLimitReached: handleQueryLimitReached,
  });

  const handleSendMessage = async (messageText?: string) => {
    if (queryLimitApiMessage !== undefined) {
      return;
    }
    const textToSend = messageText ?? input;
    const hasText = textToSend.trim().length > 0;
    const hasFile = !!selectedFile;
    if ((!hasText && !hasFile) || isFetching) {return;}

    clearedRef.current = false;

    if (!firstMessageSent) {setFirstMessageSent(true);}

    try {
      await messageHandler(hasText ? textToSend : "");
    } finally {
      // Always clear input and file regardless of success/failure
      setInput("");
      setSelectedFile(null);
      setPreviewURL(null);
    }
  };

  const createHtmlBlobUrl = (htmlString: string) => {
    const blob = new Blob([htmlString], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    return url;
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategories(prev => {
      const exists = prev.some(c => c.url === category.url);

      return exists
        ? prev.filter(c => c.url !== category.url)
        : [...prev, category];
    });
  };
  const handleGenerate = async () => {
    setIsFetching(true);
    setLoading(true);

    const payload = {
      category_links: selectedCategories.map(cat => cat.url),
      campaign_type: selectedCampaign,
      campaign_subtype: selectedSubCampaign,
      prompt,
      tone: selectedTones.map(t => t.charAt(0).toUpperCase() + t.slice(1)),
      persona,
      seasonal_tag: seasonalTag,
      cta_label: ctaLabel,
      cta_url: ctaLink,
      tenant_id: Number(localStorage.getItem("tenant_id")),
      lang: getApiLangFromI18n(i18n),
      ...(offer ? { offer } : {}),
    };

    try {
      const res = await generateTemplate(payload);

      if (!res.success) {
        setQuery(prev => [
          ...prev,
          {
            sender: "bot",
            type: "text",
            text: res.errorMessage || t("chatScreen.failedToGenerateTemplate"),
            timestamp: getTime(),
          },
        ]);
        return;
      }

      if (!res?.template && !res?.previewUrl)
        {throw new Error("No template returned");}
      if (res?.template) {setGeneratedHtml(res.template);}
      if (res?.design) {setTemplateJSON(res.design);}

      const previewUrl =
        res?.previewUrl ||
        (res?.template ? createHtmlBlobUrl(res.template) : null);
      if (!previewUrl) {throw new Error("No preview URL available");}
      setGeneratedPreviewUrl(previewUrl);

      // Update the chat UI
      setQuery(prev => [
        ...prev.filter(msg => msg.type !== "linkPreview"),
        {
          sender: "bot",
          type: "linkPreview",
          url: previewUrl,
          timestamp: getTime(),
        },
        {
          sender: "bot",
          type: "confirmSend",
          timestamp: getTime(),
        },
      ]);

      setConversationStep("confirmSend");
    } catch (err) {
      message.error(t("chatbot.generateTemplateFailed"));
      setQuery(prev => [
        ...prev,
        {
          sender: "bot",
          type: "text",
          text: t("chatScreen.failedToGenerateTemplate"),
          timestamp: getTime(),
        },
      ]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleCategoryProceed = () => {
    if (selectedCategories.length === 0) {
      const botMessage: Message = {
        sender: "bot",
        text: t("chatScreen.noCategoriesSelected"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
    } else {
      const botMessage: Message = {
        sender: "bot",
        text: `${t("chatScreen.selectedCategories")} ${selectedCategories
          .map(cat => formatCategoryName(cat.name))
          .join(", ")}`,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
    }
    const campaignMessage: Message = {
      sender: "bot",
      text: t("chatScreen.selectCampaignCategory"),
      timestamp: getTime(),
      type: "campaignOptions",
      options: campaigns,
    };
    setQuery(prev => [...prev, campaignMessage]);
    setConversationStep("selectCampaign");
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    dragged.current = false;
    dragging.current = true;

    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || e.buttons === 0) {return;}

    dragged.current = true;
    let newX = e.clientX - offset.current.x;
    let newY = e.clientY - offset.current.y;
    const el = document.querySelector(".chatbot-border") as HTMLElement;
    const elWidth = el?.offsetWidth || 120;
    const elHeight = el?.offsetHeight || 120;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    newX = Math.max(0, Math.min(screenWidth - elWidth, newX));
    newY = Math.max(0, Math.min(screenHeight - elHeight, newY));
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    dragging.current = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleClick = () => {
    if (dragged.current) {return;}
    setIsOpen(true);
  };

  const handleCampaignConfirmation = (option: string) => {
    const userMessage: Message = {
      sender: "user",
      text: option === "Yes" ? t("chatScreen.yes") : option === "No" ? t("chatScreen.no") : option,
      timestamp: getTime(),
    };
    setQuery(prev => [...prev, userMessage]);

    if (option === "Yes") {
      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.createCampaignReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };

      setQuery(prev => [...prev, botMessage]);
      setConversationStep("askName");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
    } else {
      const botMessage: Message = {
        sender: "bot",
        text: t("chatScreen.okayNoProblem"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep(null);
      setSuggestedQuestions([
        "Create New Campaign",
        "Show campaign performance",
        "View reports",
      ]);
      setInputDisabled(false);
    }
  };

  const handleSuggestedQuestionClick = (question: string) => {
    if (question === "Return on Ad Spend") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.returnOnAdSpend"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.roasReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("roasStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    if (question === "Conversions") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.conversions"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.conversionsReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("conversionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    if (question === "Impressions") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.impressions"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.impressionsReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("impressionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    if (question === "Engagement & Likes" || question === "Engagements & Likes") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.engagementAndLikes"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.engagementReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("engagementStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    // "Create New Campaign" and "Create New List" are sent to the Ask DataTram
    // API like any other prompt. The backend classifies the message (via the
    // `intent` field) and the UI reacts to that, instead of a hardcoded script.
    setInput(question);
    setSuggestedQuestions([]);
    handleSendMessage(question);
  };

  const WaveIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 35 32"
      fill="none"
    >
      <path
        d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div
      ref={chatbotRef}
      className={`chatbot-container ${isOpen ? "open" : "closed"}`}
      onClick={() => menuOpen && setMenuOpen(false)}
    >
      {!isOpen && (
        <div
          className="chatbot-border draggable tooltip-container"
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          style={{ left: position.x, top: position.y, right: "auto" }}
        >
          <img
            src="/Icons/chatbot-logo.png"
            alt="Logo"
            className="askDT-logo"
          />
          <span
            className={`tooltip-text tooltip-text--${position.x < window.innerWidth / 2 ? "left" : "right"}`}
          >
            {t("chatbot.launcherTooltip")}
          </span>
        </div>
      )}
      {isOpen && (
        <div className="chatbox">
          <div className="chatbox-header blue-border">
            <div className="chatbox-header-left">
              <img
                src="/Icons/chatbot-logo.png"
                alt="Logo"
                className="askDT-logo-inside"
              />
              <span className="chatbot-heading">{t("sidebar.sections.askDataTram")}</span>
            </div>

            <div className="chatbox-header-actions">
              <Tippy content={t("chatbot.openFullScreen")} placement="bottom">
                <Expand
                  onClick={e => {
                    e.stopPropagation();
                    const liveChatState = {
                      query,
                      firstMessageSent,
                      suggestedQuestions,
                      conversationStep,
                      campaignName,
                      audience,
                      selectedCampaign,
                      selectedSubCampaign,
                      selectedCategories,
                      generatedHtml,
                      generatedPreviewUrl,
                      templateJSON,
                    };

                    try {
                      sessionStorage.setItem(
                        "ai_chat_live_state",
                        JSON.stringify(liveChatState),
                      );
                    } catch (error) {
                      // Ignore storage failures and continue with route state.
                    }

                    navigate("/account/ai-chat", {
                      state: {
                        preserveSession: false,
                        liveChatState,
                      },
                    });
                  }}
                />
              </Tippy>

              <Tippy content={t("chatbot.moreOptions")} placement="bottom">
                <div
                  className="chatbot-menu-wrapper"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(open => !open);
                  }}
                >
                  <span className="chatbot-menu-dots">⋯</span>
                  {menuOpen && (
                    <div className="chatbot-menu-dropdown">
                      <button onClick={handleClearChat}>
                        {t("chatScreen.clearChat")}
                      </button>
                    </div>
                  )}
                </div>
              </Tippy>

              <Tippy content={t("chatbot.closeChat")} placement="bottom">
                <CloseOutlined
                  className="openclose-Icons"
                  onClick={() => setIsOpen(false)}
                />
              </Tippy>
            </div>
          </div>
          <div
            className={`conversation-area ${
              !firstMessageSent && !suggestedQuestions.length
                ? "without-suggestions"
                : ""
            }`}
          >
            {!firstMessageSent && conversationStep === null && (
              <>
                <div className="chatbot-welcome-container">
                  <div className="chatbot-avatar">
                    <Avatar
                      size={35}
                      icon={<WaveIcon />}
                      alt="Chatbot Avatar"
                    />
                  </div>
                  <div className="chatbot-welcome-message">
                    {t("chatbot.welcomeMessage")}
                  </div>
                </div>

                <div className="quick-action-buttons">
                  {quickActions.map((action, idx: number) => (
                    <button
                      key={idx}
                      className="chatbot-action-button"
                      onClick={() => handleSuggestedQuestionClick(action.value)}
                    >
                      <span className="chatbot-action-left">
                        <span className="chatbot-action-icon">{action.icon}</span>
                        <span className="chatbot-action-content">
                          <span className="chatbot-action-title">{action.title}</span>
                          <span className="chatbot-action-subtitle">
                            {action.subtitle}
                          </span>
                        </span>
                      </span>
                      <span className="arrow">
                        <img src="/Icons/smallarrow.svg" alt="arrow" />
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="chat-messages">
              {query.map((msg, index) => (
                <div key={index}>
                  {/* SHOW IMAGE FIRST if it exists */}
                  {msg.file && msg.file?.type?.startsWith("image/") && (
                    <div className={`message-row ${msg.sender}`}>
                      {msg.sender === "bot" && (
                        <div className="chatbot-avatar">
                          <Avatar
                            size={35}
                            icon={<WaveIcon />}
                            alt="Chatbot Avatar"
                          />
                        </div>
                      )}
                      <div
                        className={`message-bubble ${
                          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
                        }`}
                        style={{
                          background: "transparent",
                          padding: "0",
                          boxShadow: "none",
                        }}
                      >
                        <img
                          src={msg.file.url || undefined}
                          alt={msg.file.name}
                          className="chat-image-preview"
                          onError={e => {
                            console.error(
                              "Failed to load image:",
                              msg.file?.url,
                            );
                            e.currentTarget.style.display = "none";
                          }}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            borderRadius: "8px",
                            marginBottom: msg.text ? "12px" : "0",
                            display: "block",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* SHOW TEXT AFTER IMAGE */}
                  {msg.text && msg.type !== "limitNotice" && (
                    <div
                      className={`message-row ${msg.sender} ${
                        msg.type === "botQuestion" ? "bot-question" : ""
                      }`}
                    >
                      {msg.sender === "bot" && !msg.file && (
                        <div className="chatbot-avatar">
                          <Avatar
                            size={35}
                            icon={<WaveIcon />}
                            alt="Chatbot Avatar"
                          />
                        </div>
                      )}

                      <div
                        className={`message-bubble ${
                          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
                        }`}
                      >
                        {msg.sender === "bot" ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.text}
                          </ReactMarkdown>
                        ) : (
                          msg.text
                        )}
                      </div>
                    </div>
                  )}

                  {/* SHOW NON-IMAGE FILE LINK */}
                  {msg.file && !msg.file?.type?.startsWith("image/") && (
                    <div className={`message-row ${msg.sender}`}>
                      {msg.sender === "bot" && !msg.text && (
                        <div className="chatbot-avatar">
                          <Avatar
                            size={35}
                            icon={<WaveIcon />}
                            alt="Chatbot Avatar"
                          />
                        </div>
                      )}
                      <div
                        className={`message-bubble ${
                          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
                        }`}
                      >
                        <a
                          href={msg.file.url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="chat-file-link"
                        >
                          📎 {msg.file.name}
                        </a>
                      </div>
                    </div>
                  )}

                  {msg.type === "categoryStep" && categories.length > 0 && (
                    <div className="category-bubble-container">
                      <div className="chatbot-avatar">
                        <Avatar
                          size={35}
                          icon={<WaveIcon />}
                          alt="Chatbot Avatar"
                        />
                      </div>
                      <div className="category-selection-container">
                        <p className="message-text">
                          {t("chatScreen.fetchedCategoriesPrompt")}
                        </p>
                        {categorySearch.trim() &&
                          filteredCategoriesForSearch.length === 0 && (
                            <p className="category-search-empty" role="status">
                              {t("chatScreen.noCategoriesMatchSearch")}
                            </p>
                          )}
                        <div className="category-grid category-grid-with-search">
                          <div
                            className="category-search-cell"
                            dir={isRTL ? "rtl" : "ltr"}
                          >
                            <Search
                              size={12}
                              className="category-search-icon"
                              aria-hidden
                            />
                            <input
                              type="search"
                              className="category-search-input"
                              value={categorySearch}
                              onChange={e => setCategorySearch(e.target.value)}
                              placeholder={t("chatScreen.searchCategoriesPlaceholder")}
                              autoComplete="off"
                              spellCheck={false}
                              aria-label={t("chatbot.searchCategoriesAriaLabel")}
                            />
                          </div>
                          {visibleCategories.map((category, idx) => (
                            <div
                              key={category.url || `${category.name}-${idx}`}
                              className={`category-item ${
                                selectedCategories.some(
                                  c => c.url === category.url,
                                )
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => handleCategorySelect(category)}
                            >
                              <span className="category-name">
                                {formatCategoryName(category.name)}
                              </span>
                            </div>
                          ))}
                        </div>
                        {visibleCount < filteredCategoriesForSearch.length && (
                          <button
                            className="show-more-button"
                            onClick={() => setVisibleCount(visibleCount + 10)}
                          >
                            {t("chatScreen.showMoreCategories")}{" "}
                            <DownOutlined></DownOutlined>
                          </button>
                        )}
                        <div className="category-actions">
                          <button
                            className="proceed-button"
                            onClick={handleCategoryProceed}
                          >
                            {t("chatScreen.proceed")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Campaign options */}
                  {msg.type === "campaignOptions" && msg.options && (
                    <div className="campaign-options-grid">
                      {msg.options.map(
                        (option: { title: string }, idx: number) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() =>
                              handleSendMessage(option.title || "")
                            }
                          >
                            {translateCampaignLabel(option.title)}
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ),
                      )}
                    </div>
                  )}
                  {msg.type === "customCampaignForm" && (
                    <CustomCampaignForm
                      selectedCampaign={selectedCampaign}
                      setSelectedCampaign={setSelectedCampaign}
                      selectedSubCampaign={selectedSubCampaign}
                      setSelectedSubCampaign={setSelectedSubCampaign}
                      onAutoCorrectChange={handleAutoCorrectChange}
                      onProceed={(camp, subCamp) => {
                        const botMessage: Message = {
                          sender: "bot",
                          text: `${t("chatScreen.customCampaignCreated")} ${camp} → ${subCamp}`,
                          timestamp: getTime(),
                          type: "botQuestion",
                        };
                        const promptMessage: Message = {
                          sender: "bot",
                          timestamp: getTime(),
                          type: "promptStep",
                        };
                        setQuery(prev => [
                          ...prev,
                          botMessage,
                          promptMessage,
                        ]);
                        setConversationStep("fillPrompt");
                      }}
                      setQuery={setQuery}
                    />
                  )}

                  {/* Sub-campaign options */}
                  {msg.type === "subCampaignOptions" && selectedCampaign && (
                    <div className="campaign-options-grid">
                      {subCampaigns[selectedCampaign]?.map(
                        (
                          sub: { title: string; desc?: string },
                          idx: number,
                        ) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() => handleSendMessage(sub.title)}
                          >
                            <div>
                              <div className="subcampaign-title">
                                {translateCampaignLabel(sub.title)}
                              </div>
                            </div>
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ),
                      )}
                    </div>
                  )}
                  {msg.type === "promptStep" && (
                    <div className="promptbox-container w-[80%]">
                      <p className="promptbox-subtitle">
                        {t("chatbot.promptStep.title")}
                      </p>

                      {/* ✅ Campaign Goal (Required) */}
                      <label className="promptbox-subtitle">
                        {t("chatbot.promptStep.campaignGoalLabel")}
                        <span className="required-star">*</span>
                      </label>
                      <textarea
                        className="promptbox-textarea"
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        placeholder={t("chatbot.promptStep.campaignGoalPlaceholder")}
                      />

                      {/* ✅ Tone Presets (Required) */}
                      <div className="promptbox-tone-section">
                        <h4 className="promptbox-subtitle">
                          {t("chatbot.promptStep.tonePresets")}{" "}
                          <span className="required-star">*</span>
                        </h4>
                        <div className="promptbox-tone-buttons">
                          {TONE_OPTIONS.map((tone, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className={`promptbox-tone-btn ${
                                selectedTones.includes(tone) ? "active" : ""
                              }`}
                              onClick={() => {
                                if (selectedTones.includes(tone)) {
                                  setSelectedTones(
                                    selectedTones.filter(t => t !== tone),
                                  );
                                } else {
                                  setSelectedTones([...selectedTones, tone]);
                                }
                              }}
                            >
                              {t(
                                `chatbot.promptStep.tones.${tone.toLowerCase()}`,
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* ✅ Audience Persona */}
                      <div className="promptbox-form-section">
                        <label className="promptbox-subtitle">
                          {t("chatbot.promptStep.personaLabel")}{" "}
                          <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="promptbox-input"
                          placeholder={t("chatbot.promptStep.personaPlaceholder")}
                          onChange={e =>
                            handleAutoCorrectChange(e, setPersona)
                          }
                        />
                      </div>

                      {/* ✅ Seasonal Tag */}
                      <div className="promptbox-form-section">
                        <label className="promptbox-subtitle">
                          {t("chatbot.promptStep.seasonalTagLabel")}{" "}
                          <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="promptbox-input"
                          placeholder={t("chatbot.promptStep.seasonalTagPlaceholder")}
                          onChange={e =>
                            handleAutoCorrectChange(e, setSeasonalTag)
                          }
                        />
                      </div>

                      {/* ✅ Offer % */}
                      <div className="promptbox-form-section">
                        <label className="promptbox-subtitle">
                          {t("chatbot.promptStep.offerLabel")}
                        </label>
                        <input
                          type="text"
                          className="promptbox-input"
                          placeholder="20%"
                          onChange={e => handleAutoCorrectChange(e, setOffer)}
                        />
                      </div>

                      {/* ✅ CTA Link */}
                      <div className="promptbox-form-section">
                        <label className="promptbox-subtitle">
                          {t("chatbot.promptStep.ctaUrlLabel")}{" "}
                          <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="promptbox-input"
                          placeholder="https://"
                          onChange={e =>
                            handleAutoCorrectChange(e, setCtaLink)
                          }
                        />
                      </div>

                      {/* ✅ CTA Label */}
                      <div className="promptbox-form-section">
                        <label className="promptbox-subtitle">
                          {t("chatbot.promptStep.ctaLabelLabel")}{" "}
                          <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="promptbox-input"
                          placeholder={t("chatbot.promptStep.ctaLabelPlaceholder")}
                          onChange={e =>
                            handleAutoCorrectChange(e, setCtaLabel)
                          }
                        />
                      </div>

                      {/* ✅ Generate Button */}
                      <div className="category-actions">
                        <button
                          className="proceed-button"
                          onClick={handleGenerate}
                          disabled={loading}
                        >
                          {t("chatbot.promptStep.generateWithAi")}
                          {loading && (
                            <span className="promptbox-loading">
                              <Spin size="small" />
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {msg.type === "linkPreview" && msg.url && (
                    <div className="message-row bot">
                      <div className="chatbot-avatar">
                        <Avatar
                          size={35}
                          icon={<WaveIcon />}
                          alt="Chatbot Avatar"
                        />
                      </div>
                      <div className="message-bubble bot-bubble">
                        <p>{t("chatScreen.generatedTemplate")}</p>
                        <a
                          href={msg.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("chatScreen.previewTemplate")}
                        </a>
                      </div>
                    </div>
                  )}

                  {msg.type === "confirmCampaign" && (
                    <div className="confirm-buttons">
                      {msg.options?.map((option: string, index: number) => (
                        <button
                          key={index}
                          className={`button ${
                            option === "Yes"
                              ? "button button--primary"
                              : "button button--outline"
                          }`}
                          onClick={() => handleCampaignConfirmation(option)}
                        >
                          {option === "Yes"
                            ? t("chatScreen.yes")
                            : option === "No"
                              ? t("chatScreen.no")
                              : option}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 1: Ask if ready to send */}
                  {msg.type === "confirmSend" && (
                    <div className="message-row bot">
                      <div className="chatbot-avatar">
                        <Avatar
                          size={35}
                          icon={<WaveIcon />}
                          alt="Chatbot Avatar"
                        />
                      </div>
                      <div className="message-bubble bot-bubble">
                        <p>{t("chatScreen.readyToSend")}</p>
                        <div className="template-action-buttons">
                          <button
                            className="template-btn primary"
                            onClick={() => handleSendMessage("Yes")}
                          >
                            {t("chatScreen.yes")}
                          </button>
                          <button
                            className="template-btn secondary"
                            onClick={() => handleSendMessage("Edit")}
                          >
                            {t("chatScreen.edit")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {msg.type === "sendOption" && (
                    <div className="message-row bot">
                      <div className="chatbot-avatar">
                        <Avatar
                          size={35}
                          icon={<WaveIcon />}
                          alt="Chatbot Avatar"
                        />
                      </div>
                      <div className="message-bubble bot-bubble">
                        <p>{t("chatScreen.sendOrSchedule")}</p>
                        <div className="template-action-buttons">
                          <button
                            className="template-btn primary"
                            onClick={() => {
                              if (isSendOptionDisabled) {
                                message.warning(
                                  t("chatScreen.alreadySentWarning"),
                                );
                                return;
                              }
                              handleSendMessage("Send Now");
                              setIsSendOptionDisabled(true);
                              setIsScheduleBoxDisabled(true);
                            }}
                            disabled={isSendOptionDisabled}
                          >
                            {t("chatScreen.sendNow")}
                          </button>

                          <button
                            className="template-btn secondary"
                            onClick={() => {
                              if (isSendOptionDisabled) {
                                message.warning(
                                  t("chatScreen.alreadySentScheduleWarning"),
                                );
                                return;
                              }
                              handleSendMessage("Schedule");
                              setIsSendOptionDisabled(true);
                            }}
                            disabled={isSendOptionDisabled}
                          >
                            {t("chatScreen.schedule")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {msg.type === "schedulePicker" && (
                    <div
                      className={`schedule-picker-box ${
                        isScheduleBoxDisabled ? "disabled" : ""
                      }`}
                    >
                      <h4 className="schedule-picker-title">
                        {t("chatScreen.scheduleCampaign")}
                      </h4>
                      <p className="schedule-picker-paragraph">
                        {t("chatScreen.pakistanTime")}
                      </p>

                      {/* Preset quick scheduling options */}
                      <div className="schedule-picker-presets">
                        {(() => {
                          const today = new Date();
                          const tomorrow = new Date(today);
                          tomorrow.setDate(today.getDate() + 1);

                          const nextMonday = new Date(today);
                          const dayOffset = (8 - today.getDay()) % 7 || 7;
                          nextMonday.setDate(today.getDate() + dayOffset);

                          const formatDay = (d: Date) =>
                            d.toLocaleDateString(isArabic ? "ar-SA" : "en-US", {
                              weekday: "long",
                            });

                          const presets = [
                            {
                              label: `${t("chatScreen.tomorrowMorning")} (9:00 AM - ${formatDay(
                                tomorrow,
                              )})`,
                              time: "09:00",
                            },
                            {
                              label: `${t("chatScreen.tomorrowEvening")} (6:00 PM - ${formatDay(
                                tomorrow,
                              )})`,
                              time: "18:00",
                            },
                            {
                              label: `${t("chatScreen.nextMonday")} 9:00 AM (${formatDay(
                                nextMonday,
                              )})`,
                              time: "09:00",
                              dayOffset,
                            },
                          ];

                          return presets.map((preset, index) => (
                            <button
                              key={index}
                              className="preset-button"
                              onClick={() => {
                                if (isScheduledOrSent) {
                                  message.warning(
                                    t("chatScreen.cannotRescheduleSentCampaign"),
                                  );
                                  return;
                                }
                                handleQuickSchedule(
                                  preset,
                                  setSelectedPreset,
                                  setQuery,
                                );
                              }}
                              disabled={isScheduledOrSent}
                            >
                              {preset.label}
                            </button>
                          ));
                        })()}
                      </div>

                      {/* Manual date & time inputs */}
                      <label className="schedule-label">
                        {t("chatScreen.selectDate")}
                      </label>
                      <input
                        type="date"
                        className="schedule-input"
                        min={new Date().toISOString().split("T")[0]}
                        onChange={e => setScheduledDate(e.target.value)}
                        disabled={isScheduledOrSent}
                      />

                      <label className="schedule-label">
                        {t("chatScreen.selectTime")}
                      </label>
                      <input
                        type="time"
                        className="schedule-input"
                        onChange={e => setScheduledTime(e.target.value)}
                        disabled={isScheduledOrSent}
                      />

                      <div className="schedule-actions">
                        <button
                          className="proceed-button"
                          onClick={() => {
                            if (isScheduleBoxDisabled) {
                              message.warning(
                                t("chatScreen.cannotEditConfirmedSchedule"),
                              );
                              return;
                            }
                            handleConfirmSchedule(
                              scheduledDate,
                              scheduledTime,
                              setQuery,
                            );
                            setIsScheduleBoxDisabled(true);
                          }}
                          disabled={
                            !scheduledDate ||
                            !scheduledTime ||
                            isScheduleBoxDisabled
                          }
                        >
                          {t("chatScreen.schedule")}
                        </button>

                        <button
                          className="proceed-button"
                          onClick={handleCancelClick}
                        >
                          {t("chatScreen.cancel")}
                        </button>
                      </div>
                    </div>
                  )}

                  {msg?.type === "confirmation" && (
                    <div className="bot-message confirmation">
                      <div className="confirmation-buttons">
                        <button
                          className="yes-button"
                          onClick={() =>
                            handleScheduleConfirmation(
                              "yes",
                              selectedPreset,
                              setScheduledDate,
                              setScheduledTime,
                              setQuery,
                            )
                          }
                        >
                          {t("chatScreen.yesSchedule")}
                        </button>
                        <button
                          className="no-button"
                          onClick={() =>
                            handleScheduleConfirmation(
                              "no",
                              selectedPreset,
                              setScheduledDate,
                              setScheduledTime,
                              setQuery,
                            )
                          }
                        >
                          {t("chatScreen.noChangeTime")}
                        </button>
                      </div>
                    </div>
                  )}

                  {msg.type === "menuQuestions" && msg.menu && (
                    <div className="message bot">
                      <div className="message-text">
                        {t("chatScreen.relatedQuestions")}
                      </div>
                      <div className="menu-questions-grid">
                        {msg.menu?.questions?.map((q, idx) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() => handleSendMessage(q.text)}
                          >
                            <img
                              src={q.icon}
                              alt="q-icon"
                              className="menu-question-icon"
                            />
                            <span>{q.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {msg.type === "limitNotice" && msg.text && (
                    <div className="message-row bot system-notice-row">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble system-notice-bubble">
                        {msg.text}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* Show audience options ONLY at askAudience step */}

              {conversationStep === "askAudience" && (
                <div className="audience-selection-container">
                  {Array.isArray(audiences) && audiences.length > 0 && (
                    <>
                      <div className="audience-list-heading">
                        {t("chatScreen.selectYourList")}
                      </div>
                      {audienceListSearch.trim() &&
                        filteredExistingAudiences.length === 0 && (
                          <p className="audience-list-empty-filter" role="status">
                            {t("chatScreen.noListsMatchSearch")}
                          </p>
                        )}
                      <div className="campaign-options-grid audience-list-grid-with-search">
                        <div
                          className="audience-list-search-cell"
                          dir={isRTL ? "rtl" : "ltr"}
                        >
                          <Search
                            size={12}
                            className="audience-list-search-icon"
                            aria-hidden
                          />
                          <input
                            type="search"
                            className="audience-list-search-input"
                            value={audienceListSearch}
                            onChange={e => setAudienceListSearch(e.target.value)}
                            placeholder={t("chatScreen.searchListsPlaceholder")}
                            autoComplete="off"
                            spellCheck={false}
                            aria-label={t("chatbot.searchListsAriaLabel")}
                          />
                        </div>
                        {filteredExistingAudiences.map((audience, idx) => (
                          <button
                            key={`${audience.name}-${idx}`}
                            className="campaign-option-button"
                            /* Send the canonical `name` — segment lookup matches on it, not the label. */
                            onClick={() => handleSendMessage(audience.name)}
                          >
                            {audience.displayName}
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ))}
                      </div>
                      {/* TEMP: "Create New List" hidden in the campaign audience step (chatbot flow only). Re-enable by changing `false` back to `true`. */}
                      {false && (
                        <>
                          <div className="custom-list-heading">
                            {t("chatScreen.createCustomList")}
                          </div>
                          <div className="campaign-options-grid">
                            {audiences
                              .filter(aud => aud.name === CREATE_NEW_LIST_NAME)
                              .map((audience, idx) => (
                                <button
                                  key={idx}
                                  className="campaign-option-button"
                                  onClick={() => handleSendMessage(audience.name)}
                                >
                                  {audience.displayName}
                                  <span className="arrow">
                                    <img src="/Icons/smallarrow.svg" alt="arrow" />
                                  </span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {loading && (
                <div className="message-row bot typing-message">
                  <div className="chatbot-avatar">
                    <Avatar
                      size={35}
                      icon={<WaveIcon />}
                      alt="Chatbot Avatar"
                    />
                  </div>
                  <div className="message-bubble bot-bubble typing-bubble">
                    <span className="typing-text">{t("chatScreen.typing")}</span>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {suggestedQuestions.length > 0 && (
            <div className="compact-suggestions-container">
              <div className="compact-suggestions-header">
                {t("chatScreen.suggestedQuestionsHeader")}
              </div>
              <div className="compact-suggestions-scroll">
                {suggestedQuestions.map((question, index) => (
                  <button
                    className="compact-pill-button"
                    key={index}
                    onClick={() => handleSuggestedQuestionClick(question)}
                  >
                    {question === "Create New Campaign"
                      ? t("chatScreen.createNewCampaign")
                      : question === "Create New List"
                        ? t("chatScreen.quickActionCreateListTitle")
                        : question === "Show campaign performance"
                          ? t("chatScreen.showCampaignPerformance")
                          : question === "View reports"
                            ? t("chatScreen.viewReports")
                            : question === "Return on Ad Spend"
                              ? t("chatScreen.returnOnAdSpend")
                              : question === "Engagement & Likes" ||
                                  question === "Engagements & Likes"
                                ? t("chatScreen.engagementAndLikes")
                                : question === "Conversions"
                                  ? t("chatScreen.conversions")
                                  : question === "Impressions"
                                    ? t("chatScreen.impressions")
                                    : question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ⌨️ Input */}
          <ChatInput
            input={input}
            setInput={setInput}
            handleSendMessage={handleSendMessage}
            isFetching={isFetching}
            isInputLocked={queryLimitApiMessage !== undefined}
            inputHelperText={queryLimitApiMessage || undefined}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            previewURL={previewURL}
            setPreviewURL={setPreviewURL}
          />
        </div>
      )}
    </div>
  );
};

export default Chatbot;
